import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDate } from '../ui/format';
import { Badge } from '../ui/Page';
import {
  connectAccount,
  disconnectAccount,
  NETWORKS,
  setRequiresApproval,
  type Network,
  type SocialAccount,
} from './api';

/**
 * Connected accounts.
 *
 * Connecting a real network needs an app registered with it and a token from its OAuth flow; the
 * form takes that token as a paste for now, sends it once, and never shows it again. The "Local"
 * network is for building the calendar and the approval routine before any token exists — the
 * publisher accepts it and marks the post published without leaving the building.
 */
export function AccountsDialog({
  open,
  accounts,
  onClose,
  onChanged,
}: {
  open: boolean;
  accounts: SocialAccount[];
  onClose: () => void;
  onChanged: () => void;
}): React.JSX.Element | null {
  const dialog = useRef<HTMLDialogElement>(null);
  const [adding, setAdding] = useState(false);
  const [network, setNetwork] = useState<Network>('LOCAL');
  const [name, setName] = useState('');
  const [externalId, setExternalId] = useState('');
  const [token, setToken] = useState('');
  const [requiresApproval, setApproval] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const element = dialog.current;
    if (!element) return;

    if (open && !element.open) {
      setAdding(false);
      setError(null);
      element.showModal();
    }
    if (!open && element.open) element.close();
  }, [open]);

  const connect = useMutation({
    mutationFn: () =>
      connectAccount({
        network,
        name: name.trim(),
        externalId: externalId.trim() || null,
        requiresApproval,
        ...(token.trim() ? { config: { accessToken: token.trim() } } : {}),
      }),
    onSuccess: () => {
      setAdding(false);
      setName('');
      setExternalId('');
      setToken('');
      setError(null);
      onChanged();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const toggle = useMutation({
    mutationFn: (input: { id: string; requiresApproval: boolean }) =>
      setRequiresApproval(input.id, input.requiresApproval),
    onSuccess: onChanged,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const disconnect = useMutation({
    mutationFn: (id: string) => disconnectAccount(id),
    onSuccess: onChanged,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  if (!open) return null;

  const live = accounts.filter((account) => account.status !== 'DISABLED');

  return (
    <dialog ref={dialog} className="confirm confirm--wide" onCancel={onClose} onClose={onClose}>
      <form method="dialog" onSubmit={(event) => event.preventDefault()}>
        <h2>{t('social.accounts')}</h2>
        <p className="muted">{t('social.accountsHint')}</p>

        {live.length === 0 ? (
          <p className="muted">{t('social.noAccountsYet')}</p>
        ) : (
          <ul className="social-accounts">
            {live.map((account) => (
              <li key={account.id} className="social-account">
                <span className="cell-stack">
                  <strong>{account.name}</strong>
                  <span className="muted small">
                    {t(`social.network.${account.network}`)}
                    {account.externalId ? ` · ${account.externalId}` : ''}
                    {account.tokenExpiresAt
                      ? ` · ${t('social.tokenUntil')} ${formatDate(account.tokenExpiresAt)}`
                      : ''}
                  </span>
                </span>
                <Badge tone={account.status === 'CONNECTED' ? 'positive' : 'danger'}>
                  {t(`social.accountStatus.${account.status}`)}
                </Badge>
                <label className="cell-inline small">
                  <input
                    type="checkbox"
                    checked={account.requiresApproval}
                    disabled={toggle.isPending}
                    onChange={(event) =>
                      toggle.mutate({ id: account.id, requiresApproval: event.target.checked })
                    }
                  />
                  {t('social.requiresApproval')}
                </label>
                <button
                  type="button"
                  className="button button--quiet button--danger button--compact"
                  disabled={disconnect.isPending}
                  onClick={() => disconnect.mutate(account.id)}
                >
                  {t('social.disconnect')}
                </button>
              </li>
            ))}
          </ul>
        )}

        {adding ? (
          <div className="social-connect">
            <div className="form-grid">
              <label className="stack__field">
                <span>{t('social.networkLabel')}</span>
                <select
                  className="select"
                  value={network}
                  onChange={(event) => setNetwork(event.target.value as Network)}
                >
                  {NETWORKS.map((key) => (
                    <option key={key} value={key}>
                      {t(`social.network.${key}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="stack__field">
                <span>
                  {t('social.accountName')} <span className="required">*</span>
                </span>
                <input
                  value={name}
                  maxLength={120}
                  onChange={(event) => setName(event.target.value)}
                />
              </label>
            </div>

            {network !== 'LOCAL' && (
              <div className="form-grid">
                <label className="stack__field">
                  <span>{t(`social.externalId.${network}`)}</span>
                  <input
                    value={externalId}
                    maxLength={200}
                    onChange={(event) => setExternalId(event.target.value)}
                  />
                </label>
                <label className="stack__field">
                  <span>
                    {t('social.accessToken')} <span className="required">*</span>
                  </span>
                  <input
                    type="password"
                    value={token}
                    autoComplete="off"
                    onChange={(event) => setToken(event.target.value)}
                  />
                </label>
              </div>
            )}

            <label className="cell-inline small">
              <input
                type="checkbox"
                checked={requiresApproval}
                onChange={(event) => setApproval(event.target.checked)}
              />
              {t('social.requiresApprovalHint')}
            </label>

            {network !== 'LOCAL' && <p className="muted small">{t('social.tokenHint')}</p>}

            <div className="confirm__actions">
              <button
                type="button"
                className="button button--quiet button--compact"
                onClick={() => setAdding(false)}
              >
                {t('confirm.cancel')}
              </button>
              <button
                type="button"
                className="button button--primary button--compact"
                disabled={
                  name.trim() === '' ||
                  (network !== 'LOCAL' && token.trim() === '') ||
                  connect.isPending
                }
                onClick={() => connect.mutate()}
              >
                {t('social.connect')}
              </button>
            </div>
          </div>
        ) : (
          <div className="confirm__actions">
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={onClose}
            >
              {t('common.close')}
            </button>
            <button
              type="button"
              className="button button--primary button--compact"
              onClick={() => setAdding(true)}
            >
              {t('social.connectAccount')}
            </button>
          </div>
        )}

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}
      </form>
    </dialog>
  );
}
